/**
 * The two ACL validators (S8 Task 8.2, finding S8-08) — run in the renderer for live feedback AND in main before any
 * request. `validateFullAcl` = a complete ACL (single-path Save, recursive `set`); `validateAclPatch` = a recursive
 * change. Masks are NEVER added here (E5): a scope with named entries must carry the mask the user chose. Limits from
 * NOTES «S8: live-probed facts» A3 (32 entries per scope, base entries and the mask included) and A9.
 */
import { HNS_ACL_MAX_ENTRIES, type StorageAclChange, type StorageAclEntry, type StorageAclScope } from '../../types/azureStorage';
import { aclEntryKey as keyOf } from './azureStorageAcl';

export type AclValidation = { ok: true } | { ok: false; code: AclValidationCode; reason: string };
export type AclValidationCode = 'IncompleteAcl' | 'IncompleteDefaultAcl' | 'MaskRequired' | 'DefaultAclOnFile' | 'DuplicateAclEntry' | 'InvalidAclEntry' | 'TooManyAclEntries' | 'EmptyAclChange';

const fail = (code: AclValidationCode, reason: string): AclValidation => ({ ok: false, code, reason });
const OK: AclValidation = { ok: true };
const isNamed = (e: Pick<StorageAclEntry, 'type' | 'id'>): boolean => (e.type === 'user' || e.type === 'group') && !!e.id;
const scopeWord = (scope: StorageAclScope): string => (scope === 'access' ? 'access' : 'default');

function entryProblem(e: Pick<StorageAclEntry, 'type' | 'id'>): string | undefined {
  if (e.id === undefined || e.id === '') return undefined;
  if (e.type === 'mask' || e.type === 'other') return `A ${e.type} entry cannot name a user or group.`;
  if (/[:,\s]/u.test(e.id)) return `"${e.id}" is not a valid user or group id.`;
  return undefined;
}

/** Shared per-entry checks: ids, duplicates, the per-scope limit. */
function commonProblems(entries: readonly StorageAclEntry[]): AclValidation {
  const seen = new Set<string>();
  const perScope: Record<StorageAclScope, number> = { access: 0, default: 0 };
  for (const e of entries) {
    const problem = entryProblem(e);
    if (problem) return fail('InvalidAclEntry', problem);
    const key = keyOf({ ...e, id: e.id || undefined });
    if (seen.has(key)) return fail('DuplicateAclEntry', `The ${scopeWord(e.scope)} ${e.type} entry${e.id ? ` for ${e.id}` : ''} appears twice.`);
    seen.add(key);
    perScope[e.scope] += 1;
  }
  for (const scope of ['access', 'default'] as const) {
    if (perScope[scope] > HNS_ACL_MAX_ENTRIES) return fail('TooManyAclEntries', `The ${scopeWord(scope)} ACL has ${perScope[scope]} entries; Azure allows at most ${HNS_ACL_MAX_ENTRIES} (owner, owning group, mask and other included).`);
  }
  return OK;
}

function maskProblem(entries: readonly StorageAclEntry[], scope: StorageAclScope): AclValidation {
  const inScope = entries.filter((e) => e.scope === scope);
  if (inScope.some(isNamed) && !inScope.some((e) => e.type === 'mask')) {
    return fail('MaskRequired', `Choose the mask for the ${scopeWord(scope)} entries; Azure would otherwise recalculate it on every path.`);
  }
  return OK;
}

/**
 * A `modify` keeps every entry it does not name, and owner, owning group and other always exist — in the access scope,
 * and in a default scope the patch writes to (it is absent or complete, 8.0.3) — so the smallest possible result is the
 * patch plus the base entries it omits (review astra-A #2: 29 named users + a mask is 33 entries on every path).
 */
function patchSizeProblem(entries: readonly StorageAclEntry[]): AclValidation {
  for (const scope of ['access', 'default'] as const) {
    const inScope = entries.filter((e) => e.scope === scope);
    if (scope === 'default' && inScope.length === 0) continue;
    const kept = (['user', 'group', 'other'] as const).filter((type) => !inScope.some((e) => e.type === type && !e.id)).length;
    const size = inScope.length + kept;
    if (size > HNS_ACL_MAX_ENTRIES) {
      return fail('TooManyAclEntries', `The ${scopeWord(scope)} ACL would have at least ${size} entries (owner, owning group and other are always kept); Azure allows at most ${HNS_ACL_MAX_ENTRIES} (owner, owning group, mask and other included).`);
    }
  }
  return OK;
}

const hasBase = (entries: readonly StorageAclEntry[], scope: StorageAclScope): boolean =>
  (['user', 'group', 'other'] as const).every((type) => entries.some((e) => e.scope === scope && e.type === type && !e.id));

/** A complete ACL for one path (Save) or for every path (recursive `set`). */
export function validateFullAcl(entries: readonly StorageAclEntry[], options: { isDirectory: boolean }): AclValidation {
  const common = commonProblems(entries);
  if (!common.ok) return common;
  if (!hasBase(entries, 'access')) return fail('IncompleteAcl', 'The ACL needs exactly one owner (user::), owning group (group::) and other (other::) entry.');
  const defaults = entries.filter((e) => e.scope === 'default');
  if (defaults.length > 0) {
    if (!options.isDirectory) return fail('DefaultAclOnFile', 'Default entries apply to directories only; a file cannot have a default ACL.');
    if (!hasBase(entries, 'default')) return fail('IncompleteDefaultAcl', 'A default ACL needs default owner, owning group and other entries (or no default entries at all).');
  }
  const accessMask = maskProblem(entries, 'access');
  return accessMask.ok ? maskProblem(entries, 'default') : accessMask;
}

/** A recursive change: `set` = the full-ACL rules (defaults allowed); `modify` = any subset with explicit masks; `remove` = named entries only. */
export function validateAclPatch(change: StorageAclChange): AclValidation {
  if (change.entries.length === 0) return fail('EmptyAclChange', 'Choose at least one entry.');
  if (change.mode === 'set') return validateFullAcl(change.entries, { isDirectory: true });
  if (change.mode === 'modify') {
    const common = commonProblems(change.entries);
    if (!common.ok) return common;
    const size = patchSizeProblem(change.entries);
    if (!size.ok) return size;
    const accessMask = maskProblem(change.entries, 'access');
    return accessMask.ok ? maskProblem(change.entries, 'default') : accessMask;
  }
  const seen = new Set<string>();
  for (const e of change.entries) {
    if (e.type !== 'user' && e.type !== 'group') return fail('InvalidAclEntry', 'Owner, owning group, other and mask entries cannot be removed; replace the ACL instead.');
    if (!e.id) return fail('InvalidAclEntry', 'Owner, owning group, other and mask entries cannot be removed; replace the ACL instead.');
    const problem = entryProblem(e);
    if (problem) return fail('InvalidAclEntry', problem);
    const key = keyOf(e);
    if (seen.has(key)) return fail('DuplicateAclEntry', `The ${scopeWord(e.scope)} ${e.type} entry for ${e.id} appears twice.`);
    seen.add(key);
  }
  return OK;
}
